import { Component, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { type VehicleTypeUpdate, type VehicleType } from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { createUuid } from "../../utils/uuid";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { store } from "../../store";

export interface FormUpdateVehicleTypeProps extends FormUpdateProps<VehicleType> {}

@observer
export class FormUpdateVehicleType extends Component<FormUpdateVehicleTypeProps> {
    private validationId = createUuid();

    constructor(props: FormUpdateVehicleTypeProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryVehicleTypes.validation.update.updateModel(
            this.validationId,
            this.vehicleTypeUpdate,
        );
    }

    componentDidMount(): void {
        store.repositoryVehicleTypes.validation.update.initializeModel(
            this.validationId,
            this.vehicleTypeUpdate,
            this.props.id,
        );
    }

    public componentWillUnmount(): void {
        store.repositoryVehicleTypes.discardMutableCopy(FormUpdateVehicleType.name, this.props.id);
    }

    @computed private get vehicleType(): VehicleType | undefined {
        return store.repositoryVehicleTypes.mutableCopyById(
            FormUpdateVehicleType.name,
            this.props.id,
        );
    }

    @computed private get vehicleTypeUpdate(): VehicleTypeUpdate {
        return pick(["label"], this.vehicleType);
    }

    @action.bound
    private async updateVehicleType(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const vehicleType = await store.repositoryVehicleTypes.update(
            this.props.id,
            this.vehicleTypeUpdate,
        );
        if (this.props.onUpdate) {
            this.props.onUpdate(vehicleType);
        }
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateVehicleType.submit.text")}
                validation={store.repositoryVehicleTypes.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateVehicleType}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formUpdateVehicleType.label.label")}
                        disabled={!this.vehicleType}
                        {...doubleBindString(this.vehicleType!, "label", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicleTypes.validation.update.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdateVehicleType.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
